import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const Callback = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const error = urlParams.get('error'); // Check for error from OAuth provider

    // Function to send message to opener window
    const sendResultToOpener = (data) => {
      if (window.opener) {
        // The targetOrigin should be the origin of your main app, 
        // but '*' can be used for simplicity during development if you're sure about security.
        // For production, specify the exact origin e.g., https://app.prompteh.com
        window.opener.postMessage({ message: "deliverResult", result: data }, '*');
        // It's often good practice to close the popup after sending the message,
        // though react-simple-oauth2-login might also handle this.
        // window.close(); 
      }
    };

    // Listen for a request from the opener window (react-simple-oauth2-login)
    const handleMessage = (event) => {
      // Ensure the message is from a trusted source if not using a specific origin in postMessage from opener
      // For react-simple-oauth2-login, it doesn't specify its origin when it posts "requestResult"
      // so we primarily check the message type.
      if (event.data && event.data.message === "requestResult") {
        if (code) {
          sendResultToOpener({ code });
        } else if (error) {
          sendResultToOpener({ error: error, error_description: urlParams.get('error_description') || 'Login failed' });
        } else {
           sendResultToOpener({ error: 'unknown_error', error_description: 'No code or error received in callback.' });
        }
        // Once we've posted the message, remove the listener
        window.removeEventListener("message", handleMessage);
      }
    };

    window.addEventListener("message", handleMessage);

    // If this page is not in a popup or the popup is not using postMessage (e.g. same-origin)
    // or if an immediate error is present, handle it directly.
    // The react-simple-oauth2-login's onSuccess/onFailure will be triggered by postMessage.
    // So, the original fetch logic here will only run if not handled by postMessage.
    // We need to ensure this logic doesn't run if postMessage is used.
    // A simple way is to check if window.opener exists. If it does, it's likely a popup.
    // The `react-simple-oauth2-login` will initiate the postMessage communication if `isCrossOrigin` is true.
    // If it's same-origin, it reads `popup.location.href` and `Callback.jsx` doesn't need to do postMessage.

    if (!window.opener) { // If not in a popup, or if the popup is not expected to use postMessage
      if (code) {
        // Exchange code for token
        fetch('https://auth.prompteh.com/token', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ code }),
        })
          .then(response => response.json())
          .then(data => {
            if (data.error) { // Handle token exchange errors
              throw new Error(data.error_description || data.error);
            }
            // Store the token
            localStorage.setItem('auth_token', data.token);
            
            // Fetch user data if needed
            return fetch('https://auth.prompteh.com/me', {
              headers: {
                'Authorization': `Bearer ${data.token}`,
              },
            });
          })
          .then(response => response.json())
          .then(userData => {
            if (userData.error) { // Handle user fetch errors
              throw new Error(userData.error_description || userData.error);
            }
            localStorage.setItem('user', JSON.stringify(userData));
            navigate('/');
          })
          .catch(err => {
            console.error('Authentication error:', err);
            navigate(`/login?error=authentication_failed&error_description=${encodeURIComponent(err.message)}`);
          });
      } else if (error) {
        console.error('OAuth error:', error, urlParams.get('error_description'));
        navigate(`/login?error=${encodeURIComponent(error)}&error_description=${encodeURIComponent(urlParams.get('error_description') || 'Login failed')}`);
      } else if (!window.opener) { // Only navigate if not in a popup that might be handled by postMessage
        navigate('/login?error=no_code&error_description=Authorization%20code%20was%20not%20received.');
      }
    }
    
    // Cleanup listener when component unmounts
    return () => {
      window.removeEventListener("message", handleMessage);
    };

  }, [navigate]); // `code` and `error` are not needed in dependency array as they are read fresh

  return (
    <div className="flex items-center justify-center h-screen">
      <div className="text-center">
        <h2 className="text-xl mb-4">Processing your login...</h2>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
      </div>
    </div>
  );
};

export default Callback;
